import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ToasterService } from "angular2-toaster";
import { WidgetBase } from "./widget-base";
import { WidgetEventStruct } from "../widget-event.struct";
import { BatchEntity } from "../../entities/batch.entity";
import { WidgetEnum } from "../../enums";
import { BsModalRef } from "ngx-bootstrap/modal";
import { BsModalService } from "ngx-bootstrap/modal";
import { ModalOptions } from "ngx-bootstrap/modal";

@Component({
  selector: "batch-widget",
  template: `
    <ng-container class="row" *ngIf="entity">
      <ng-template #standard>
        <!-- Modal Header -->
        <div
          class="modal-header bg-light d-flex justify-content-between align-items-center"
        >
          <h4 class="modal-title m-0 h5">{{ currentModal?.title }}</h4>
          <button
            type="button"
            class="close"
            aria-label="Close"
            (click)="deviceTypeListModalRef.hide()"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Error Message -->
        <div
          *ngIf="currentModal?.errorMsg"
          class="alert alert-danger mx-3 mt-3 mb-0 py-2"
        >
          <i class="fas fa-exclamation-circle me-2"></i
          >{{ currentModal?.errorMsg }}
        </div>

        <!-- Loading State -->
        <div class="card mb-3 mx-3 mt-3" *ngIf="!entity">
          <div
            class="card-header d-flex justify-content-between align-items-center bg-light"
          >
            <strong>Batch:</strong> Loading
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div class="card-body text-center py-4">
            <widget-spinner></widget-spinner>
          </div>
        </div>

        <!-- Content -->
        <div
          class="card mx-3 my-3"
          [ngStyle]="layout != 'sidebar' ? { 'min-height': '400px' } : null"
        >
          <div
            class="card-header bg-light d-flex justify-content-between align-items-center"
          >
            <h5 class="m-0">
              Batch #{{ entity.identifier }} - {{ entity.name }}
            </h5>
          </div>

          <div class="card-body">
            <div class="row">
              <!-- Left Column -->
              <div class="col-md-8 pe-md-4">
                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold text-muted">
                    Identifier:
                  </div>
                  <div class="col-md-8">
                    {{ entity.identifier }}
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold text-muted">
                    Created On:
                  </div>
                  <div class="col-md-8">
                    {{ entity.createdOn | date : "mediumDate" }}
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold text-muted">
                    Modified On:
                  </div>
                  <div class="col-md-8">
                    {{ entity.modifiedOn | date : "mediumDate" }}
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold text-muted">Name:</div>
                  <div class="col-md-8">
                    <ng-container
                      *ngIf="options && options['edit']; else viewName"
                    >
                      <input
                        type="text"
                        class="form-control"
                        [(ngModel)]="entity.name"
                        (ngModelChange)="updateBatch()"
                      />
                    </ng-container>
                    <ng-template #viewName>
                      {{ entity.name }}
                    </ng-template>
                  </div>
                </div>

                <div class="row mb-4">
                  <div class="col-md-4 font-weight-bold text-muted">
                    Notes:
                  </div>
                  <div class="col-md-8">
                    <ng-container
                      *ngIf="options && options['edit']; else viewNotes"
                    >
                      <textarea
                        class="form-control"
                        rows="2"
                        [(ngModel)]="entity.notes"
                        (ngModelChange)="updateBatch()"
                      ></textarea>
                    </ng-container>
                    <ng-template #viewNotes>
                      {{ entity.notes || "No notes available" }}
                    </ng-template>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <embed-device-attributes
                      [entity]="entity"
                      [layout]="'card'"
                      [options]="
                        options?.attributes || {
                          title: 'Attributes',
                          edit: options['edit']
                        }
                      "
                    ></embed-device-attributes>
                  </div>
                </div>
              </div>

              <!-- Right Column -->
              <div class="col-md-4 border-start ps-md-4 mt-4 mt-md-0">
                <entity-notes-v2
                  [title]="'Batch Notes'"
                  [entity]="entity.sref()"
                  [options]="{ expand: true, dragula: false }"
                  [placeholder]="'Batch Staff Note.'"
                ></entity-notes-v2>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="card-footer bg-light" *ngIf="options['edit']">
            <div class="d-flex justify-content-between">
              <div>
                <button
                  *ngIf="entity.meta['hasChanges']"
                  class="btn btn-warning me-2"
                  (click)="saveBatch($event)"
                >
                  <i class="fas fa-save me-2"></i>Save Changes
                </button>
              </div>
              <div class="d-flex justify-content-start">
                <button
                  *ngIf="currentModal?.cancelMsg"
                  class="btn btn-outline-secondary mx-2"
                  (click)="currentModal?.cancel()"
                >
                  {{ currentModal?.cancelMsg }}
                </button>
                <button
                  class="btn btn-outline-danger"
                  (click)="deleteBatch($event)"
                >
                  <i class="fas fa-trash-alt me-2"></i>Delete Batch
                </button>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
      <ng-container [ngSwitch]="layout">
        <ng-container *ngSwitchCase="'table'">
          <!-- Table Header -->
          <div
            class="table-header bg-light py-2 border-bottom"
            *ngIf="options['header']"
          >
            <div class="row g-0 align-items-center font-weight-bold">
              <div class="col-1 ps-2">#Identifier</div>
              <div class="col-2">Name</div>
              <div class="col">Notes</div>
              <div class="col-2">Created On</div>
              <div class="col-2">Modified On</div>
            </div>
          </div>

          <!-- Table Row -->
          <div
            class="table-row py-2 border-bottom"
            [ngClass]="{
              'div-table-tr-alt': options['index'] % 2 == 1,
              'div-table-tr': options['index'] % 2 == 0
            }"
          >
            <div
              class="row g-0 align-items-center"
              (click)="showEditModal(standard, entity, $event)"
            >
              <!-- Identifier Column -->
              <div class="col-1 ps-2 text-truncate" [title]="entity.identifier">
                <a href="#" class="text-decoration-none">
                  #{{ entity.identifier }}
                </a>
              </div>

              <!-- Name Column -->
              <div class="col-2 text-truncate" [title]="entity.name">
                {{ entity.name }}
              </div>

              <!-- Notes Column -->
              <div class="col text-truncate" [title]="entity.notes">
                {{ entity.notes }}
              </div>

              <!-- Created On Column -->
              <div
                class="col-2 text-truncate"
                [title]="entity.createdOn | date : 'longDate'"
              >
                {{ entity.createdOn | date : "mediumDate" }}
              </div>

              <!-- Modified On Column -->
              <div
                class="col-2 text-truncate"
                [title]="entity.modifiedOn | date : 'longDate'"
              >
                {{ entity.modifiedOn | date : "mediumDate" }}
              </div>
            </div>
          </div>
        </ng-container>

        <ng-container *ngSwitchCase="'shadowbox'">
          <ng-container *ngIf="entity.meta['new']">
            <div class="row mb-3">
              <div class="col-md-3 font-weight-bold">Identifier:</div>
              <div class="col-md-9">
                <ng-container *ngIf="options?.edit">
                  <input
                    type="number"
                    class="form-control"
                    [(ngModel)]="entity.identifier"
                    (ngModelChange)="updateBatch()"
                  />
                </ng-container>
                <ng-container *ngIf="!options?.edit">
                  {{ entity.identifier }}
                </ng-container>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-3 font-weight-bold">Name:</div>
              <div class="col-md-9">
                <ng-container *ngIf="options?.edit">
                  <input
                    type="text"
                    class="form-control"
                    [(ngModel)]="entity.name"
                    (ngModelChange)="updateBatch()"
                  />
                </ng-container>
                <ng-container *ngIf="!options?.edit">
                  {{ entity.name }}
                </ng-container>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-3 font-weight-bold">Notes:</div>
              <div class="col-md-9">
                <ng-container *ngIf="options?.edit">
                  <input
                    type="text"
                    class="form-control"
                    [(ngModel)]="entity.notes"
                    (ngModelChange)="updateBatch()"
                  />
                </ng-container>
                <ng-container *ngIf="!options?.edit">
                  {{ entity.notes }}
                </ng-container>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-12">
                <embed-device-attributes
                  [entity]="entity"
                  [layout]="'card'"
                  [options]="
                    options?.attributes || {
                      title: 'Attributes',
                      edit: options?.edit
                    }
                  "
                ></embed-device-attributes>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="!entity.meta['new']">
            <div class="row">
              <div class="col-md-8">
                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold">Identifier:</div>
                  <div class="col-md-8 text-truncate">
                    {{ entity.identifier }}
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold">Created On:</div>
                  <div class="col-md-8">
                    {{ entity.createdOn | date : "mediumDate" }}
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold">Modified On:</div>
                  <div class="col-md-8">
                    {{ entity.modifiedOn | date : "mediumDate" }}
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold">Name:</div>
                  <div class="col-md-8">
                    <ng-container *ngIf="options?.edit">
                      <input
                        type="text"
                        class="form-control"
                        [(ngModel)]="entity.name"
                        (ngModelChange)="updateBatch()"
                      />
                    </ng-container>
                    <ng-container *ngIf="!options?.edit">
                      {{ entity.name }}
                    </ng-container>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-4 font-weight-bold">Notes:</div>
                  <div class="col-md-8">
                    <ng-container *ngIf="options?.edit">
                      <input
                        type="text"
                        class="form-control"
                        [(ngModel)]="entity.notes"
                        (ngModelChange)="updateBatch()"
                      />
                    </ng-container>
                    <ng-container *ngIf="!options?.edit">
                      {{ entity.notes }}
                    </ng-container>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-12">
                    <embed-device-attributes
                      [entity]="entity"
                      [layout]="'card'"
                      [options]="options?.attributes || { title: 'Attributes' }"
                    ></embed-device-attributes>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <entity-notes-v2
                  [title]="'Batch Notes'"
                  [entity]="entity.sref()"
                  [options]="{ expand: true, dragula: false }"
                  [placeholder]="'Batch Staff Note.'"
                ></entity-notes-v2>
              </div>
            </div>
          </ng-container>
        </ng-container>

        <ng-container *ngSwitchDefault>
          <div
            class="card"
            [ngStyle]="layout != 'sidebar' ? { height: '100%' } : null"
          >
            <div class="card-header">
              Batch #{{ entity.identifier }} - {{ entity.name }}
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-8">
                  <div class="row">
                    <div class="col"><b>Identifier:</b></div>
                    <div class="col">{{ entity.identifier }}</div>
                  </div>
                  <div class="row">
                    <div class="col"><b>Created On:</b></div>
                    <div class="col">
                      {{ entity.createdOn | date : "mediumDate" }}
                    </div>
                  </div>
                  <div class="row">
                    <div class="col"><b>Modified On:</b></div>
                    <div class="col">
                      {{ entity.modifiedOn | date : "mediumDate" }}
                    </div>
                  </div>
                  <div class="row">
                    <div class="col"><b>Name:</b></div>
                    <div class="col">
                      <ng-container *ngIf="options && options['edit']">
                        <input
                          (ngModelChange)="updateBatch()"
                          type="text"
                          [(ngModel)]="entity.name"
                        />
                      </ng-container>
                      <ng-container *ngIf="!(options && options['edit'])">
                        {{ entity.name }}
                      </ng-container>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col"><b>Notes:</b></div>
                    <div class="col">
                      <ng-container *ngIf="options && options['edit']">
                        <input
                          (ngModelChange)="updateBatch()"
                          type="text"
                          [(ngModel)]="entity.notes"
                        />
                      </ng-container>
                      <ng-container *ngIf="!(options && options['edit'])">
                        {{ entity.notes }}
                      </ng-container>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <embed-device-attributes
                        [entity]="entity"
                        [layout]="'card'"
                        [options]="
                          options?.attributes || {
                            title: 'Attributes',
                            edit: options['edit']
                          }
                        "
                      ></embed-device-attributes>
                    </div>
                  </div>
                </div>
                <div class="col">
                  <entity-notes-v2
                    [title]="'Batch Notes'"
                    [entity]="entity.sref()"
                    [options]="{ expand: true, dragula: false }"
                    [placeholder]="'Batch Staff Note.'"
                  ></entity-notes-v2>
                </div>
              </div>
            </div>
            <div class="card-footer" *ngIf="options['edit']">
              <div
                *ngIf="entity.meta['hasChanges']"
                class="btn btn-warning btn-lg mr-2"
                (click)="saveBatch($event)"
              >
                Save
              </div>
              <div class="btn btn-danger btn-lg" (click)="deleteBatch($event)">
                Delete
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </ng-container>

    <ng-template #standard>
      <div
        class="card"
        [ngStyle]="layout != 'sidebar' ? { height: '100%' } : null"
      >
        <div class="card-header">
          Batch #{{ entity.identifier }} - {{ entity.name }}
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-8">
              <div class="row">
                <div class="col"><b>Identifier:</b></div>
                <div class="col">{{ entity.identifier }}</div>
              </div>
              <div class="row">
                <div class="col"><b>Created On:</b></div>
                <div class="col">
                  {{ entity.createdOn | date : "mediumDate" }}
                </div>
              </div>
              <div class="row">
                <div class="col"><b>Modified On:</b></div>
                <div class="col">
                  {{ entity.modifiedOn | date : "mediumDate" }}
                </div>
              </div>
              <div class="row">
                <div class="col"><b>Name:</b></div>
                <div class="col">
                  <ng-container *ngIf="options && options['edit']">
                    <input
                      (ngModelChange)="updateBatch()"
                      type="text"
                      [(ngModel)]="entity.name"
                    />
                  </ng-container>
                  <ng-container *ngIf="!(options && options['edit'])">
                    {{ entity.name }}
                  </ng-container>
                </div>
              </div>
              <div class="row">
                <div class="col"><b>Notes:</b></div>
                <div class="col">
                  <ng-container *ngIf="options && options['edit']">
                    <input
                      (ngModelChange)="updateBatch()"
                      type="text"
                      [(ngModel)]="entity.notes"
                    />
                  </ng-container>
                  <ng-container *ngIf="!(options && options['edit'])">
                    {{ entity.notes }}
                  </ng-container>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <embed-device-attributes
                    [entity]="entity"
                    [layout]="'card'"
                    [options]="
                      options?.attributes || {
                        title: 'Attributes',
                        edit: options['edit']
                      }
                    "
                  ></embed-device-attributes>
                </div>
              </div>
            </div>
            <div class="col">
              <entity-notes-v2
                [title]="'Batch Notes'"
                [entity]="entity.sref()"
                [options]="{ expand: true, dragula: false }"
                [placeholder]="'Batch Staff Note.'"
              ></entity-notes-v2>
            </div>
          </div>
        </div>
        <div class="card-footer" *ngIf="options['edit']">
          <div
            *ngIf="entity.meta['hasChanges']"
            class="btn btn-warning btn-lg mr-2"
            (click)="saveBatch($event)"
          >
            Save
          </div>
          <div class="btn btn-danger btn-lg" (click)="deleteBatch($event)">
            Delete
          </div>
        </div>
      </div>
    </ng-template>
  `,
})
export class BatchWidgetComponent extends WidgetBase implements OnInit {
  public editMode = false;
  @Input() layout: string = null;
  @Input() entity: BatchEntity;
  @Input() options: any;
  @Output() widgetEvent = new EventEmitter<WidgetEventStruct>();
  forwardEvent(e, widgetEvent: WidgetEventStruct) {
    e.preventDefault();
    this.widgetEvent.emit(widgetEvent);
  }

  public deviceTypeListModalRef: BsModalRef;
  public currentModal: any;

  constructor(
    public toasterService: ToasterService,
    public modalService: BsModalService
  ) {
    super();
  }

  public showEditModal(modal, entry: BatchEntity, event) {
    event.preventDefault();
    this.currentModal = this.editTypeModal;
    let config: ModalOptions = {
      backdrop: "static",
      keyboard: false,
      class: "lg-modal",
    };
    this.deviceTypeListModalRef = this.modalService.show(modal, config);
  }

  public editTypeModal = {
    title: "Batch",
    msg: `Batch.`,
    errorMsg: null,
    processing: false,
    form: "edit-attribute",
    cancelMsg: "Cancel",
    cancel: () => {
      this.editTypeModal.errorMsg = null;
      this.deviceTypeListModalRef.hide();
    },
  };

  public select(event) {
    let a = new WidgetEventStruct();
    a.event_body = this.entity;
    a.event_type = "select_entity";
    a.event_widget_type = WidgetEnum.EMBED_WIDGET__BATCH_ENTITY;
    this.forwardEvent(event, a);
  }

  deleteBatch(event = null) {
    if (event) {
      event.preventDefault();
    }
    this.entity
      .delete()
      .then((r) => {
        this.toasterService.pop("success", "Success", `Batch Deleted`);
        this.entity.meta.hasChanges = false;
      })
      .catch((r) => {
        this.toasterService.pop("warning", "Error", `Batch Delete Failed`);
      });
  }

  saveBatch(event = null) {
    if (event) {
      event.preventDefault();
    }
    this.entity
      .update()
      .then((r) => {
        this.toasterService.pop("success", "Success", `Batch Updated`);
        this.entity.meta.hasChanges = false;
      })
      .catch((r) => {
        this.toasterService.pop("warning", "Error", `Batch Update Failed`);
      });
  }

  updateBatch() {
    this.entity.meta.hasChanges = true;
  }

  ngOnInit(): void {}
}
